import { authApi } from '@/features/auth/api/authApi';
import type { User } from '@/features/auth/types';

type ManagerPermissionKey =
  | 'manageResidents'
  | 'managePayments'
  | 'viewReports'
  | 'postAnnouncements'
  | 'approveApplications';

/**
 * Hook to get current logged-in user's permissions
 *
 * For managers: Returns their specific permissions
 * For owners/admins: Returns all permissions enabled
 * For other roles: Returns all permissions disabled
 */
export const useCurrentUserPermissions = () => {
  // Get user from localStorage (synchronous)
  const user: User | null = authApi.getStoredUser();

  // Determine permissions based on role
  const getPermissions = () => {
    if (!user) {
      return {
        manageResidents: false,
        managePayments: false,
        viewReports: false,
        postAnnouncements: false,
        approveApplications: false,
      };
    }

    // Owners and Admins have all permissions
    if (user.role === 'OWNER' || user.role === 'ADMIN') {
      return {
        manageResidents: true,
        managePayments: true,
        viewReports: true,
        postAnnouncements: true,
        approveApplications: true,
      };
    }

    // Managers use their specific permissions
    if (user.role === 'MANAGER') {
      return (
        user.permissions || {
          manageResidents: true,
          managePayments: true,
          viewReports: true,
          postAnnouncements: true,
          approveApplications: false,
        }
      );
    }

    // Other roles (RESIDENT, PENDING) have no permissions
    return {
      manageResidents: false,
      managePayments: false,
      viewReports: false,
      postAnnouncements: false,
      approveApplications: false,
    };
  };

  const permissions = getPermissions();

  return {
    user,
    permissions,
    /**
     * Check if user has a specific permission
     */
    hasPermission: (permission: ManagerPermissionKey): boolean => {
      return permissions[permission] ?? false;
    },
    /**
     * Check if user has ALL of the specified permissions
     */
    hasAllPermissions: (...permissionKeys: ManagerPermissionKey[]): boolean => {
      return permissionKeys.every((key) => permissions[key] ?? false);
    },
    /**
     * Check if user has ANY of the specified permissions
     */
    hasAnyPermission: (...permissionKeys: ManagerPermissionKey[]): boolean => {
      return permissionKeys.some((key) => permissions[key] ?? false);
    },
    /**
     * Check if user is owner or admin (has all permissions)
     */
    isOwnerOrAdmin: user?.role === 'OWNER' || user?.role === 'ADMIN',
  };
};
